import { twMerge } from 'tailwind-merge';

import { IStorePackage } from 'models/store/i-store-package.interface';

import { usePackageCheckout } from 'contexts/PackageCheckoutContext';
import { useScrollContext } from 'contexts/ScrollContext';

import { NftPackage } from '../components/NftPackage';
import { Pagination } from 'components/pagination';

import { usePackageEvents } from 'hooks/events/usePackageEvents';

import styles from './StoreDigitalAssetSection.module.scss';

type StoreDigitalAssetSectionProps = {
  title: string;
  productName: string;
  suffix?: string;
  description?: string;
  packages: IStorePackage[];
  currentPage: number;
  totalPages: number;
  shouldShowPagination: boolean;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
  goToPage: (page: number) => void;
  goToNextPage: () => void;
  goToPreviousPage: () => void;
  className?: string;
};

export const SectionTitle = ({
  title,
  productName,
  suffix,
  titleColor = 'tw-text-white',
  productNameColor = 'tw-text-[#6dfbff]',
}: {
  title: string;
  productName: string;
  suffix?: string;
  titleColor?: string;
  productNameColor?: string;
}) => (
  <h1 className={styles['main-title']}>
    <span className={twMerge(titleColor, 'tw-uppercase')}>{title} </span>
    <span className={twMerge(productNameColor, 'tw-uppercase')}>{productName}</span>
    {suffix && <span className={twMerge('tw-text-white', 'tw-uppercase')}>{suffix}</span>}
  </h1>
);

const StoreDigitalAssetSection = ({
  title,
  productName,
  suffix,
  description,
  packages,
  currentPage,
  totalPages,
  shouldShowPagination,
  hasNextPage,
  hasPreviousPage,
  goToPage,
  goToNextPage,
  goToPreviousPage,
  className,
}: StoreDigitalAssetSectionProps) => {
  const { sectionRefs } = useScrollContext();
  const { openCheckout } = usePackageCheckout();
  const packageEvents = usePackageEvents();

  if (packages.length === 0) {
    return null;
  }

  const onClickBuy = (packageData: IStorePackage) => {
    openCheckout(packageData, packageEvents);
  };

  return (
    // <section className="tw-w-full tw-bg-[#122f6d] tw-py-5">
    <div className={twMerge(className)}>
      <section className={styles['featured-section']} ref={sectionRefs.digitalAssetPackages}>
        <div className={styles['header-section']}>
          <SectionTitle title={title} productName={productName} suffix={suffix} />
          {description && <h2 className={twMerge(styles['sub-title'], 'tw-max-w-[700px]')}>{description}</h2>}
        </div>

        <div className="tw-grid tw-grid-cols-2 tw-justify-items-center tw-gap-4 lg:tw-flex lg:tw-flex-row lg:tw-items-center lg:tw-justify-center">
          {packages.map((packageItem) => (
            <NftPackage
              key={packageItem.id}
              packageData={packageItem}
              onClickBuy={onClickBuy}
              packageImageCountClassName="tw-text-[16px] tw-leading-[16px] lg:tw-text-[37.7px] lg:tw-leading-[36.4px]"
              packageImageCountSubtitleClassName="tw-text-[6px] lg:tw-text-[14px] lg:tw-font-semibold"
              displayPrice
            />
          ))}
        </div>

        {shouldShowPagination && (
          <>
            <div className="tw-mb-12" />
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              hasNextPage={hasNextPage}
              hasPreviousPage={hasPreviousPage}
              goToPage={goToPage}
              goToNextPage={goToNextPage}
              goToPreviousPage={goToPreviousPage}
            />
          </>
        )}
      </section>
    </div>
  );
};

export default StoreDigitalAssetSection;
